import { GeoCoordinates } from '@aloft/toolkit-shared';
import { Block, Flex, Input, RadioGroup, Typography } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { MILES_TO_METERS } from '../../../map/constants.ts';
import { useFilterCompMap } from '../../hooks/useFilterCompMap.ts';
import { CompSelectionProperty, RecommendCompsFormSchema } from '../../types.ts';
import { MapAlerts } from '../details/MapAlerts.tsx';
import { useWatch } from 'react-hook-form';

export const FilterDrawerMapView = ({
  subjectComp: { latitude: subjectLat = 0, longitude: subjectLng = 0 },
}: {
  subjectComp: CompSelectionProperty;
}) => {
  const { control, setValue } = useZodFormContext<RecommendCompsFormSchema>();
  // Caching the radius value to keep the value when swtiching between radius and polygon search types
  const geoSearch = useWatch({ name: 'search.geoSearch', control });
  const [radius, setRadius] = useState<'' | number | undefined>();
  const [mapType, setMapType] = useState<string>('satellite');
  useEffect(() => {
    if (geoSearch.t === 'radius') {
      if (radius === undefined) {
        setRadius(geoSearch.radiusInMeters / MILES_TO_METERS); // Convert meters to miles
      }
    }
  }, [geoSearch.t]);

  const handleRadiusChange = (value: '' | number | undefined) => {
    if (typeof value === 'number') {
      if (value < 0) {
        setRadius(0);
      } else if (value > 100) {
        setRadius(100);
      } else {
        setRadius(value);

        const radiusInMeters = value * MILES_TO_METERS;
        if (geoSearch.t === 'radius') {
          setValue('search.geoSearch.radiusInMeters', radiusInMeters);
        }
      }
    } else {
      setRadius(value);
    }
  };

  const onPolygonCreateAndUpdate = (coordinates: GeoCoordinates) => {
    setValue('search.geoSearch', {
      t: 'polygon',
      coords: coordinates,
    });
  };
  const onPolygonDrawDelete = () => {
    setValue('search.geoSearch', {
      t: 'polygon',
      coords: [],
    });
  };

  const handleGeoSearchTypeChange = (value: string) => {
    if (value === 'radius') {
      setValue('search.geoSearch', {
        t: 'radius',
        radiusInMeters: radius ? radius * MILES_TO_METERS : MILES_TO_METERS,
        coord: [subjectLng, subjectLat],
      });
    } else {
      setValue('search.geoSearch', {
        t: 'polygon',
        coords: [],
      });
    }
  };

  const { mapContainerRef } = useFilterCompMap({
    subjectLat,
    subjectLng,
    lat: subjectLat,
    lng: subjectLng,
    geoSearch,
    mapType,
    onPolygonCreateAndUpdate,
    onPolygonDrawDelete,
  });

  const handleChangeMapType = (value: string) => {
    setMapType(value);
  };

  return (
    <Block height="100%" width="100%" position="relative">
      <MapAlerts geoSearch={geoSearch} />
      <Flex position="absolute" bottom="40px" left="20px">
        <Block zIndex="1" backgroundColor="surface1" padding="10px" borderRadius="8px">
          <Flex flexDirection="row">
            <RadioGroup
              value={geoSearch.t}
              items={[
                {
                  value: 'radius',
                  label: 'Radius',
                },
                { value: 'polygon', label: 'Polygon' },
              ]}
              size="md"
              onChange={handleGeoSearchTypeChange}
            />
            <Flex flexDirection="row">
              <Block width="40px" marginRight="5px">
                <Input
                  type="number"
                  size="xs"
                  id="radius"
                  min={0}
                  max={100}
                  placeholderText="Radius"
                  value={radius}
                  onChange={handleRadiusChange}
                  onClear={() => setRadius('')}
                />
              </Block>
              <Typography>miles</Typography>
            </Flex>
          </Flex>
        </Block>
      </Flex>
      <Flex position="absolute" bottom="40px" right="20px">
        <Block zIndex="1" backgroundColor="surface1" padding="10px" borderRadius="8px">
          <RadioGroup
            value={mapType}
            items={[
              { value: 'satellite', label: 'Satellite' },
              { value: 'street', label: 'Street' },
            ]}
            size="md"
            onChange={handleChangeMapType}
          />
        </Block>
      </Flex>
      <Block ref={mapContainerRef} height="100%" width="100%" />
    </Block>
  );
};
